import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { X, Youtube, FileText, Video, Loader, Download } from 'lucide-react';
import {
  finishProcessing,
  useIframeResizer,
  AppStoreBadge,
  GooglePlayBadge,
} from './common';
import { useIntentTranslation, getIntentFromUrl } from './intentTranslations';

interface ImportURLState {
  url: string;
  isImporting: boolean;
  progress: number;
  importComplete: boolean;
  previewType: 'youtube' | 'file' | null;
  previewId: string | null;
  thumbnailUrl: string | null;
  title: string | null;
  error: string | null;
}

const URL_PATTERNS = {
  youtube: {
    patterns: [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ],
    getThumbnail: (id: string) =>
      `https://img.youtube.com/vi/${id}/default.jpg`,
    getTitle: async (id: string) => {
      try {
        const response = await fetch(
          `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`,
        );
        const data = await response.json();
        return data.title || null;
      } catch {
        return null;
      }
    },
  },
  file: {
    patterns: [/\.(mp4|mov|avi|wmv|flv|mkv|webm|mp3|wav|ogg|m4a)$/i],
  },
};

const extractUrlInfo = async (
  url: string,
): Promise<{
  type: ImportURLState['previewType'];
  id: string | null;
  title: string | null;
  thumbnailUrl: string | null;
}> => {
  try {
    if (!url || !url.trim()) {
      return { type: null, id: null, title: null, thumbnailUrl: null };
    }

    const matchedPlatform = Object.entries(URL_PATTERNS).find(([, config]) =>
      config.patterns.some((pattern) => {
        const match = url.match(pattern);
        return !!match;
      }),
    );

    if (matchedPlatform) {
      const [platform, config] = matchedPlatform;
      const pattern = config.patterns.find((p) => url.match(p));

      if (!pattern) {
        return { type: null, id: null, title: null, thumbnailUrl: null };
      }

      const match = url.match(pattern);

      if (!match || !match[1]) {
        const id = platform === 'file' ? url : null;
        return {
          type: platform as ImportURLState['previewType'],
          id,
          title: null,
          thumbnailUrl: null,
        };
      }

      const id = match[1];
      let title = null;
      let thumbnailUrl = null;

      if (platform === 'youtube' && id) {
        title = await URL_PATTERNS.youtube.getTitle(id);
        thumbnailUrl = URL_PATTERNS.youtube.getThumbnail(id);
      }

      return {
        type: platform as ImportURLState['previewType'],
        id,
        title,
        thumbnailUrl,
      };
    }

    return { type: null, id: null, title: null, thumbnailUrl: null };
  } catch (error) {
    console.error('Error extracting URL info:', error);
    return { type: null, id: null, title: null, thumbnailUrl: null };
  }
};

const getPlatformIcon = (type: ImportURLState['previewType']) => {
  switch (type) {
    case 'youtube':
      return <Youtube className="w-4 h-4" />;
    case 'file':
      return <FileText className="w-4 h-4" />;
    default:
      return null;
  }
};

const getPlatformLabel = (
  type: ImportURLState['previewType'],
  t: (key: string) => string,
) => {
  switch (type) {
    case 'youtube':
      return t('platforms.youtube');
    case 'file':
      return t('platforms.file');
    default:
      return '';
  }
};

const BASE_MOBILE_URL = 'https://screenapp.io/mobile';

interface ImportURLProps {
  showIOSBadge?: boolean;
}

const ImportURL: React.FC<ImportURLProps> = ({ showIOSBadge = true }) => {
  const { t: originalT } = useTranslation();
  const [intent, setIntent] = useState<string | null>(getIntentFromUrl());
  const { t } = useIntentTranslation(intent, 'importURL');
  useIframeResizer();
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [state, setState] = useState<ImportURLState>({
    url: '',
    isImporting: false,
    progress: 0,
    importComplete: false,
    previewType: null,
    previewId: null,
    thumbnailUrl: null,
    title: null,
    error: null,
  });

  useEffect(() => {
    const fetchUrlInfo = async () => {
      const { type, id, title, thumbnailUrl } = await extractUrlInfo(state.url);
      if (type && id) {
        setState((prev) => ({
          ...prev,
          previewType: type,
          previewId: id,
          thumbnailUrl: thumbnailUrl || null,
          title: title || null,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          previewType: null,
          previewId: null,
          thumbnailUrl: null,
          title: null,
        }));
      }
    };
    fetchUrlInfo();
  }, [state.url]);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));

    // Focus input on load
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const newIntent = getIntentFromUrl();
      setIntent(newIntent);
    };

    window.addEventListener('hashchange', handleHashChange);
    const currentIntent = getIntentFromUrl();
    if (currentIntent !== intent) {
      setIntent(currentIntent);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [intent]);

  const handleUrlImport = () => {
    if (state.url) {
      try {
        if (!state.previewType && state.url) {
          try {
            Boolean(new URL(state.url));
          } catch {
            setErrorMessage(
              originalT('importURL.invalidURL') || 'Invalid URL format',
            );
            return;
          }
        }

        setState((prev) => ({
          ...prev,
          isImporting: true,
          progress: 0,
          importComplete: false,
        }));
        let currentProgress = 0;
        const interval = setInterval(() => {
          currentProgress += 10;
          setState((prev) => ({
            ...prev,
            progress: Math.min(currentProgress, 100),
          }));
          if (currentProgress >= 100) {
            clearInterval(interval);
            setState((prev) => ({
              ...prev,
              importComplete: true,
              isImporting: false,
            }));
          }
        }, 20);
      } catch (error) {
        console.error('Error in handleUrlImport:', error);
        setErrorMessage(
          originalT('importURL.generalError') || 'An error occurred',
        );
        setState((prev) => ({ ...prev, isImporting: false }));
      }
    }
  };

  useEffect(() => {
    if (state.importComplete && state.url) {
      const timer = setTimeout(() => {
        finishProcessing(state.url);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [state.importComplete, state.url]);

  const handleDismissPreview = () => {
    setState((prev) => ({
      ...prev,
      url: '',
      previewType: null,
      previewId: null,
      thumbnailUrl: null,
      title: null,
    }));
  };

  const handleErrorClose = () => {
    setErrorMessage(null);
  };

  const getMobileUrl = () => {
    const hashPart = window.location.hash.split('?')[1] || '';
    const urlParams = new URLSearchParams(hashPart);
    const urlIntent = urlParams.get('intent') || '';

    const url = new URL(BASE_MOBILE_URL);
    url.searchParams.append('utm_source', urlIntent || 'website');
    url.searchParams.append('utm_medium', 'microtools');
    url.searchParams.append('utm_campaign', 'import_url_cta');

    return url.toString();
  };

  const renderIcon = (size: number) => {
    if (state.isImporting) {
      return (
        <Loader className={`w-${size} h-${size} animate-spin text-primary`} />
      );
    }

    if (state.importComplete) {
      if (state.thumbnailUrl) {
        return (
          <img
            src={state.thumbnailUrl}
            alt={`${state.previewType} thumbnail`}
            className={`w-${size} h-${Math.round(size * 0.6)} object-cover rounded-lg flex-shrink-0`}
          />
        );
      }

      const getPlatformStyling = () => {
        switch (state.previewType) {
          case 'youtube':
            return 'bg-gradient-to-br from-red-600 to-red-700';
          case 'file':
            return 'bg-gradient-to-br from-indigo-500 to-indigo-600';
          default:
            return 'bg-gradient-to-br from-gray-600 to-gray-700';
        }
      };

      return (
        <div
          className={`w-${size} h-${Math.round(size * 0.6)} rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center text-white ${getPlatformStyling()}`}
        >
          {getPlatformIcon(state.previewType) || (
            <Video
              className={`w-${Math.round(size * 0.3)} h-${Math.round(size * 0.3)}`}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const renderContent = (isMobile = false) => {
    if (state.isImporting) {
      return (
        <>
          <p
            className={`${isMobile ? 'text-base' : 'text-lg'} font-medium mb-1`}
          >
            {t('processingURL')}
          </p>
          <p
            className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}
          >
            {Math.round(state.progress)}% complete
          </p>
        </>
      );
    }

    if (state.importComplete) {
      return (
        <>
          <p
            className={`${isMobile ? 'text-sm' : 'text-lg'} font-medium mb-1 truncate`}
          >
            {state.title ||
              getPlatformLabel(state.previewType, t) ||
              t('complete')}
          </p>
          <div className="flex items-center gap-2">
            <p
              className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}
            >
              Ready to process • {t('processing')}
            </p>
            <Loader className="w-3 h-3 animate-spin text-muted-foreground" />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="flex justify-center">
      <div className="p-3 sm:p-6 max-w-5xl w-full">
        {/* Only show input interface when not importing and not complete */}
        {!state.isImporting && !state.importComplete && (
          <>
            <div className="w-full space-y-4">
              {/* Mobile: Stacked Layout */}
              <div className="sm:hidden space-y-3">
                <Input
                  ref={inputRef}
                  placeholder={t('placeholder')}
                  value={state.url}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, url: e.target.value }))
                  }
                  className="h-14 rounded-full pl-4 pr-4 text-base bg-white border-none"
                />

                {/* Import Button */}
                <Button
                  onClick={handleUrlImport}
                  disabled={!state.url || state.isImporting}
                  className="w-full h-12 rounded-full text-base font-medium flex items-center justify-center gap-2"
                >
                  {intent === 'video-downloader' && (
                    <Download className="w-4 h-4" />
                  )}
                  {t('importButton').replace(/ /g, '\u00A0')}
                </Button>
              </div>

              {/* Desktop: Inline Layout */}
              <div className="hidden sm:block relative">
                <Input
                  placeholder={t('placeholder')}
                  value={state.url}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, url: e.target.value }))
                  }
                  className="h-14 rounded-full pr-36 pl-4 text-base bg-white border-none"
                />

                {/* Import Button */}
                <Button
                  onClick={handleUrlImport}
                  disabled={!state.url || state.isImporting}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-10 min-w-24 px-4 rounded-full text-base font-medium flex items-center gap-2"
                >
                  {intent === 'video-downloader' && (
                    <Download className="w-4 h-4" />
                  )}
                  {t('importButton').replace(/ /g, '\u00A0')}
                </Button>
              </div>

              {/* Preview Card */}
              {state.previewType && (
                <Card
                  className="border-2 cursor-pointer hover:border-primary transition-colors"
                  onClick={handleUrlImport}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {state.thumbnailUrl ? (
                        <img
                          src={state.thumbnailUrl}
                          alt={`${state.previewType} thumbnail`}
                          className="w-20 h-12 object-cover rounded-md flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-12 flex items-center justify-center bg-muted rounded-md flex-shrink-0">
                          {getPlatformIcon(state.previewType)}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {state.title ||
                            getPlatformLabel(state.previewType, t)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {getPlatformLabel(state.previewType, t)}
                        </p>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissPreview();
                        }}
                        className="flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Always show mobile badge below on separate row */}
            {(isIOS || isAndroid) && showIOSBadge && (
              <div className="flex justify-center mt-6">
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-muted-foreground">
                    {originalT('tools.mobileappcta.orUseApp')}
                  </p>

                  {isIOS && (
                    <AppStoreBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={44}
                    />
                  )}

                  {isAndroid && (
                    <GooglePlayBadge
                      mobileUrl={getMobileUrl()}
                      showLabel={false}
                      height={44}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Inline Import Progress and Completion */}
        {(state.isImporting || state.importComplete) && (
          <div>
            <Card
              className={`rounded-xl ${
                state.importComplete
                  ? 'bg-gradient-to-br from-green-50 to-green-100'
                  : 'bg-gradient-to-br from-blue-50 to-blue-100'
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-6 mb-4">
                  {renderIcon(16)}
                  <div className="flex-1 min-w-0">{renderContent(false)}</div>
                </div>

                {state.isImporting && (
                  <Progress value={state.progress} className="h-2" />
                )}
              </CardContent>
            </Card>

            {/* Cancel button */}
            <div className="flex justify-center mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setState({
                    url: '',
                    isImporting: false,
                    progress: 0,
                    importComplete: false,
                    previewType: null,
                    previewId: null,
                    thumbnailUrl: null,
                    title: null,
                    error: null,
                  });
                }}
                className="text-xs text-muted-foreground"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {state.error && (
          <p className="text-destructive mt-4 text-sm">{state.error}</p>
        )}

        {errorMessage && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
            <Alert className="w-auto">
              <AlertDescription>
                {errorMessage}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleErrorClose}
                  className="ml-2 p-0 h-auto"
                >
                  <X className="w-4 h-4" />
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportURL;
